/* cliente.ts — o ÚNICO lugar do front que chama fetch.
 *
 * Componente não fala com a rede; fala com um hook, que fala com daqui. Isso
 * dá um lugar só para: base da URL, cookie de sessão, formato de erro e
 * timeout. */

/** Erro devolvido pela API no formato { erro: { codigo, mensagem } }. */
export class ErroApi extends Error {
  readonly codigo: string;
  readonly status: number;
  readonly detalhes: unknown;

  constructor(codigo: string, mensagem: string, status: number, detalhes?: unknown) {
    super(mensagem);
    this.name = 'ErroApi';
    this.codigo = codigo;
    this.status = status;
    this.detalhes = detalhes;
  }

  /** Sessão ausente ou expirada: quem chama decide se cria sessão anônima. */
  get naoAutenticado(): boolean {
    return this.status === 401;
  }

  /** Erro do cliente (4xx): repetir a chamada não adianta. */
  get culpaDoCliente(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

// Em desenvolvimento SEMPRE passamos pelo proxy do Vite (/api → :3000): mesma
// origem, cookie httpOnly viaja sem CORS. Em produção o Fastify serve o dist
// e responde /api na mesma origem — VITE_API_URL só existe para o caso de a
// API ficar em outro domínio.
const BASE = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_URL ?? '/api');

const PRAZO_PADRAO = 15_000;

export type OpcoesRequisicao = {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  sinal?: AbortSignal;
  prazoMs?: number;
  parametros?: Record<string, string | number | boolean | undefined>;
};

function montarUrl(caminho: string, parametros?: OpcoesRequisicao['parametros']): string {
  const url = `${BASE}${caminho.startsWith('/') ? caminho : `/${caminho}`}`;
  if (!parametros) return url;
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) {
    if (valor !== undefined) busca.set(chave, String(valor));
  }
  const q = busca.toString();
  return q ? `${url}?${q}` : url;
}

export async function requisitar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const { metodo = 'GET', corpo, sinal, prazoMs = PRAZO_PADRAO, parametros } = opcoes;

  // Timeout próprio: rede pendurada não pode deixar a tela em "carregando"
  // para sempre. AbortSignal.any junta o nosso prazo ao cancelamento do Query.
  const doPrazo = AbortSignal.timeout(prazoMs);
  const combinado = sinal ? AbortSignal.any([sinal, doPrazo]) : doPrazo;

  let resposta: Response;
  try {
    resposta = await fetch(montarUrl(caminho, parametros), {
      method: metodo,
      // A sessão é cookie httpOnly: sem isto, o navegador não o envia.
      credentials: 'include',
      headers: corpo === undefined ? {} : { 'content-type': 'application/json' },
      body: corpo === undefined ? null : JSON.stringify(corpo),
      signal: combinado,
    });
  } catch (causa) {
    if (doPrazo.aborted) {
      throw new ErroApi('tempo_esgotado', 'O servidor demorou demais para responder.', 0, causa);
    }
    // Cancelamento legítimo (troca de tela) tem de subir como está, senão o
    // React Query o trata como falha e mostra erro para o usuário.
    if (sinal?.aborted) throw causa;
    throw new ErroApi('sem_conexao', 'Não foi possível falar com o servidor.', 0, causa);
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const dados: unknown = texto ? seguroJson(texto) : null;

  if (!resposta.ok) {
    const erro = extrairErro(dados);
    throw new ErroApi(
      erro?.codigo ?? `http_${resposta.status}`,
      erro?.mensagem ?? mensagemPadrao(resposta.status),
      resposta.status,
      dados,
    );
  }

  return dados as T;
}

function seguroJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
}

function extrairErro(dados: unknown): { codigo?: string; mensagem?: string } | null {
  if (typeof dados !== 'object' || dados === null) return null;
  const envelope = (dados as { erro?: unknown }).erro;
  if (typeof envelope !== 'object' || envelope === null) return null;
  const { codigo, mensagem } = envelope as { codigo?: unknown; mensagem?: unknown };
  return {
    ...(typeof codigo === 'string' ? { codigo } : {}),
    ...(typeof mensagem === 'string' ? { mensagem } : {}),
  };
}

function mensagemPadrao(status: number): string {
  if (status === 401) return 'Sessão expirada.';
  if (status === 403) return 'Você não tem acesso a este dado.';
  if (status === 404) return 'Não encontrado.';
  if (status >= 500) return 'O servidor falhou. Tente de novo em instantes.';
  return 'Não foi possível concluir a operação.';
}

export const api = {
  obter: <T>(caminho: string, opcoes?: Omit<OpcoesRequisicao, 'metodo' | 'corpo'>) =>
    requisitar<T>(caminho, { ...opcoes, metodo: 'GET' }),
  criar: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, metodo: 'POST', corpo }),
  salvar: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, metodo: 'PUT', corpo }),
  ajustar: <T>(caminho: string, corpo?: unknown, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, metodo: 'PATCH', corpo }),
  remover: <T>(caminho: string, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, metodo: 'DELETE' }),
};

/** Mensagem legível de qualquer erro — para toast e caixa de erro. */
export function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message;
  if (erro instanceof Error) return erro.message;
  return 'Erro inesperado.';
}
